import { useEffect, useSyncExternalStore } from 'react'
import dayjs from 'dayjs'
import { api } from '../services/API'

/**
 * Contadores do menu lateral. Hoje só há um com dados reais no backend:
 * Finanças · N unidades em atraso (`unidadesEmAtraso` do painel-resumo, à data
 * de hoje). Os restantes (reservas por confirmar, pagamentos por aprovar…)
 * precisam de endpoints de contagem — ver docs/ux-audit/PENDING-BACKEND.md.
 */
export interface NavBadges {
  financeOverdueUnits?: number
}

const cache = new Map<string, NavBadges>()
const inFlight = new Set<string>()
const listeners = new Set<() => void>()
const EMPTY: NavBadges = {}

function load(condominioId: string) {
  if (cache.has(condominioId) || inFlight.has(condominioId)) return
  inFlight.add(condominioId)
  // O endpoint exige um período; o número de unidades em atraso não depende dele.
  const start = dayjs().startOf('month')
  const prev = start.subtract(1, 'month')
  api.get<{ unidadesEmAtraso?: number }>('/api/cobrancas/painel-resumo', {
    params: {
      condominioId,
      de: start.format('YYYY-MM-DD'),
      ate: start.endOf('month').format('YYYY-MM-DD'),
      dePrevio: prev.format('YYYY-MM-DD'),
      atePrevio: prev.endOf('month').format('YYYY-MM-DD'),
    },
    suppressForbiddenToast: true,
  })
    .then((r) => cache.set(condominioId, { financeOverdueUnits: r.data?.unidadesEmAtraso ?? 0 }))
    .catch(() => cache.set(condominioId, EMPTY))
    .finally(() => {
      inFlight.delete(condominioId)
      listeners.forEach((l) => l())
    })
}

export function useNavBadges(condominioId: string | undefined, enabled: boolean): NavBadges {
  useEffect(() => {
    if (enabled && condominioId) load(condominioId)
  }, [condominioId, enabled])

  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l) } },
    () => (enabled && condominioId ? cache.get(condominioId) ?? EMPTY : EMPTY),
  )
}
